import _ from 'lodash';
import moment from 'moment';

import spinner from 'components/sharpr/spinner/SpinnerSingleton';
import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { PostEditBaseCtrl } from 'components/sharpr/post/service/PostEditBaseCtrl';
import { ensure } from 'components/sharpr/ensure/ensure';
import { PostTypes } from '../../../sharpr/post/postType/PostTypesEnum';
import PostAction from '../../../../../a-react-app/src/_common/domain/enums/PostAction.js';

export function PostEditRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.PostEdit', {
		url: '/post/:id/edit',
		controllerAs: 'vm',
		controller: PostEditCtrl,
		templateUrl: '/ng/hub/Post/Edit/PostEditCtrl.html?' + window.BUILD,
		permission: ['view_post'], //if you put edit_post here that blocks to many users because member and clients can edit their own posts
		resolve: {
			$title: () => 'Edit Post',
			$style: () => AssetManager.css('/ng/hub/Post/Edit/PostEditCtrl.css'),
			post: ApiResourcify.endpoint('/v3/posts/:id'),
			attachments: ApiResourcify.endpoint('/v3/posts/:id/attachments/details'),
			submissionMedia: ApiResourcify.endpoint('/v2/submission_media/labels', {
				cacheFor: 30 * 60 * 1000,
			}),
		},
	});
}

class PostEditCtrl extends PostEditBaseCtrl {
	constructor(
		Identity,
		Api,
		Theme,
		Cfg,
		AutoArchive,
		FileDrag,
		Post, // the actual Post service
		$q,
		$timeout,
		$interval,
		post, // from the resolve on the route
		attachments,
		submissionMedia,
		modal
	) {
		'ngInject';

		super();

		// save injections
		this.Api = Api;
		this.Identity = Identity;
		this.Theme = Theme;
		this.Cfg = Cfg;
		this.AutoArchive = AutoArchive;
		this.FileDrag = FileDrag;
		this.Post = Post;
		this.$q = $q;
		this.$timeout = $timeout;
		this.$interval = $interval;
		this.post = post;
		this.attachments = attachments;
		this.submissionMedia = submissionMedia;
		this.modal = modal;

		this.setup();
	}

	async setup() {
		await super.setup();
		this.mode = 'edit';
		this.loadTaxonomy();
		this.setupResearchManagement();
		this._setupPost();
		this._setupDependentData();
		this.setupLabels();
		this._setupPreferences();
		this.chosenAccessRule = this.post.access_rule;
		this.selectedAccessRule = this.post.access_rule;
		this.accountId = this.Identity.currentAffiliationId();
		this.canAddToInsightBrief =
			this.post?.access_rule_id === null || this.post?.allowedActions?.includes(PostAction.AddToInsightBrief);
	}

	_setupPreferences() {
		this.showClientTabOption = this.Cfg.readAsBool('UI.post_show_clients_users');
	}

	loadTaxonomy() {
		const onLoad = this._setupTaxonomy.bind(this);
		return super.loadTaxonomy(onLoad, () => {});
	}

	_setupDependentData() {
		this.Api.get('/countries').then(countries => {
			this.countries = ensure.array(countries);
			this.countries.unshift({ code: 'us', name: 'United States' });
		});
	}

	_setupPost() {
		// set category id to zero if not set
		if (!this.post.category) {
			this.post.category = { id: 0 };
		}
		// format attachments how <sh-upload> needs them
		ensure.array(this.attachments).forEach(att => {
			att.href = att.url;
			att.size = att.bytes;
			att.name = att.filename;
			att.icon = att.icon_font_class;
		});
		// keep a copy of orig post so we can patch only changed items
		this.origPost = _.cloneDeep(this.post);
		// normalize entities that php encodes that Quill does not
		this.origPost.rich_text_body = this.origPost.rich_text_body.replace(/&quot;/g, '"').replace(/&#039;/g, "'");
		this.origAttachments = _.cloneDeep(this.attachments);
		// make a copy of dependent data
		this.chosenFileIds = this.attachments.map(a => a.id);
		this.classification = _.cloneDeep(this.post.classification);
		if (this.post.image) {
			this.image = _.cloneDeep(this.post.image);
			this.image.url += '.fit-235x135.jpg';
			this.origImageUrl = this.image.url;
		} else {
			this.origImageUrl = undefined;
			this.image = undefined;
			if (this.post.attachment_thumbnails && this.post.attachment_thumbnails.length) {
				this.filePreview = this.post.attachment_thumbnails[0];
			}
		}
		// create labels for our typeahead for submitted by and published by
		// even if they are not in the "popular" list
		if (this.post.submitted_by) {
			this.post.submitted_by = {
				...this.post.submitted_by,
				$label: `${this.post.submitted_by.fname} ${this.post.submitted_by.lname}`,
			};
		}
		if (this.post.published_by) {
			this.post.published_by = {
				...this.post.published_by,
				$label: `${this.post.published_by.fname} ${this.post.published_by.lname}`,
			};
		}
		const modifier = ensure.object(this.post.modified_by);
		this.modifiedByName = `${modifier.fname || ''} ${modifier.lname || ''}`.trim() || 'API';
		// grab AI tags and keywords (this may take a little while)
		this.Api.get(`/posts/${this.post.id}/keywords`).then(keywords => {
			this.keywords = ensure.array(keywords);
			this.origKeywords = _.cloneDeep(this.keywords);
		});
		this.Api.get(`/posts/${this.post.id}/summary`).then(summary => {
			this.summary = ensure.string(summary);
			this.origSummary = this.summary;
		});
	}

	_setupTaxonomy() {
		ensure.array(this.post.tags).forEach(tag => {
			tag.$search = tag.name;
		});
	}

	onBeforeModalExit() {
		if (this.isUploading()) {
			return this.pleaseWaitForUpload();
		} else if (!this.isSaving && this.isChanged()) {
			return this.modal.confirm({
				text: 'You have unsaved changes.',
				confirmText: 'Discard',
				cancelText: 'Continue Editing',
			});
		}
		super.onExit();
	}

	save() {
		if (this.isUploading()) {
			return this.pleaseWaitForUpload();
		}

		if (!this.isChanged()) {
			return this.closeModal();
		}

		if (!this.post.published_at) {
			this.post.published_at = moment().format();
		}

		const validation = this.validatePost(this.post);
		if (validation.success) {
			this.patch()
				.then(results => {
					try {
						// handle callbacks from cake pages that pop up edit screen (e.g. story edit page)
						// or react pages (e.g. dashboard, insights)
						const search = window.location.hash.match(/\?.+/);

						if (search) {
							const urlParams = new URLSearchParams(search[0]);
							const parentCallback = urlParams.get('callback');
							const postMessage = urlParams.get('postMessage');
							const postBackFullPost = urlParams.get('postBackFullPost');
							const redirectUrl = this.Cfg.read('Features.url_to_open_after_post_submit');

							if (redirectUrl) {
								window.location.href = `${redirectUrl}/${this.post.id}`;
							} else if (parentCallback) {
								window.parent.rintel[parentCallback.replace('rintel.')]();
							} else if (postMessage) {
								window.parent.postMessage(postMessage, '*');
							} else if (postBackFullPost && postBackFullPost === '1') {
								const data = JSON.stringify({ action: 'editFullPost', post: this.post });
								window.parent.postMessage(data, '*');
							}
						}

						// let the calling window know the post was patched successfully
						// used for cross communication between iframe and hosting page
						window.parent.postMessage({ action: 'success', postId: this.post.id }, '*');
					} catch (e) {
						console.error('error posting message to parent', e);
					}

					if (
						this.post.type.id === PostTypes.research_brief &&
						ensure.object(this.origPost.classification).name &&
						this.origPost.classification.name !== 'Completed' &&
						ensure.object(this.classification).name &&
						this.classification.name === 'Completed'
					) {
						// for Completed RM posts, show completed post dialog (The one shown is specific to JTI for now)
						this.Post.viewJtiCompletePost();
					}

					this.closeModal();
				})
				.catch(err => {
					this.isSaving = false;
					this.handleErrors(err);
				});
		} else {
			spinner.stopAll();
			this.showErrorsModal(validation.errors);
		}
	}

	closeModal() {
		//For react
		window.parent.postMessage('close_modal', '*');
		if (this.$reject) {
			// from angular
			this.$reject();
			return;
		}
		try {
			// from cake (e.g. story edit page)
			window.parent.$.RiModal.get('top').close();
		} catch (e) {}
	}

	patch() {
		let fieldsDiff = this._getFieldsDiff();
		const attDiff = this._getAttachmentsDiff();
		const aiKeywordsDiff = this._getAiKeywordsDiff();
		const taxonomyOptionsDiff = this._getTaxonomyOptionsDiff();
		const tasks = [];
		const attachmentData = [];
		// approval status; this overwrites previous values even if not explicitly sent.
		if (this.Cfg.read('Features.post_approval')) {
			fieldsDiff.approval_status = this.Identity.isApprover() ? 'approved' : 'unapproved';
		}

		if (_.size(fieldsDiff) > 0) {
			tasks.push(
				this.Api.request('PATCH', `/posts/${this.post.id}`, fieldsDiff).then(res => {
					if (res.status > 399) {
						return Promise.reject(JSON.parse(res.headers('api-response-errors') || ''));
					} else {
						return res;
					}
				})
			);
		}

		const origUrls = _.map(this.origAttachments, 'url');
		this.attachments.forEach(att => {
			const doesAttachmentExist = origUrls.indexOf(att.url) === -1;
			if (!doesAttachmentExist) {
				attachmentData.push({ attachment_id: att.id });
				return;
			}

			let attData = {
				attachment_id: att.id,
				// if it has .href prop, it's a plupload file. Needs to be moved to permanent location.
				data: att.href || undefined,
				name: att.name,
				size: att.size,
				created_at: att.date,
			};
			attachmentData.push(attData);
			tasks.push(
				this.Api.create(`/posts/${this.post.id}/attachments`, attData).then(newId => {
					if (newId) {
						attData.attachment_id = newId;
					}
				})
			);
		});

		attDiff.toRemove.forEach(attId => {
			tasks.push(this.Api.delete(`/posts/${this.post.id}/attachments/${attId}`));
		});

		aiKeywordsDiff.toRemove.forEach(kw => {
			tasks.push(this.Api.delete(`/posts/${this.post.id}/keywords/${encodeURIComponent(kw.keyword)}`));
		});

		if (taxonomyOptionsDiff.length > 0) {
			tasks.push(
				this.Api.patch(`/posts/${this.post.id}/taxonomies`, {
					selected_option_ids: taxonomyOptionsDiff,
				})
			);
		}

		if (this.origSummary != this.summary) {
			tasks.push(this.Api.patch(`/posts/${this.post.id}/summary`, { text: this.summary }));
		}

		this.isSaving = true;

		return Promise.all(tasks).then(results => {
			// put attachments into the correct order
			// regardless if some are new or existing
			if (attDiff.shouldUpdateSort) {
				const ids = attachmentData.map(a => a.attachment_id);
				return this.Api.put(`/posts/${this.post.id}/attachments/sort`, { ids });
			}

			return results;
		});
	}

	isChanged() {
		const fieldsDiff = this._getFieldsDiff();
		const attDiff = this._getAttachmentsDiff();
		const aiKeywordsDiff = this._getAiKeywordsDiff();
		const taxonomiesChanged = this._getTaxonomyOptionsDiff();
		const isAiSummaryChanged = this.origSummary != this.summary;
		return (
			_.size(fieldsDiff) > 0 ||
			attDiff.toAdd.length > 0 ||
			attDiff.toRemove.length > 0 ||
			attDiff.shouldUpdateSort ||
			aiKeywordsDiff.toRemove.length > 0 ||
			isAiSummaryChanged ||
			taxonomiesChanged.length > 0
		);
	}

	insightChanged() {
		// when insight changes, update the publisher
		if (this._autoChangedPublisher) {
			// only change once
			// (e.g. user may change insight text then go edit insight by then edit insight text again)
			return;
		}
		this._autoChangedPublisher = true;
		const me = this.Identity.user();
		const existingContributor = _.findWhere(this.topContributors, { id: me.id });
		if (existingContributor) {
			// set the publisher to my existing record in topContributors
			this.post.published_by == existingContributor;
		} else {
			// create a top contributor for me
			const newContributor = {
				id: me.id,
				$label: `${me.fname_preferred || me.fname} ${me.lname_preferred || me.lname}`,
			};
			// add me to the top of the top contributors if i'm not already there
			this.topContributors.unshift(newContributor);
			// set the publisher to this new topContributor entry
			this.post.published_by = newContributor;
		}
	}

	_getFieldsDiff() {
		const scalarFields = [
			'access_rule_id',
			'approval_status',
			'intel_text',
			'headline',
			'rich_text_body',
			'source_link',
			'source_name',
			'source_author',
			'is_client_intel',
			'submitter_country',
			'submitter_region',
			'submitter_city',
		];
		const toPatch = {};
		scalarFields.forEach(field => {
			if (this.origPost[field] !== this.post[field]) {
				toPatch[field] = this.post[field];
			}
		});
		if (ensure.object(this.origPost.submitted_by).id !== ensure.object(this.post.submitted_by).id) {
			toPatch.submitted_by_id = this.post.submitted_by.id;
		}
		if (ensure.object(this.origPost.published_by).id !== ensure.object(this.post.published_by).id) {
			toPatch.published_by_id = this.post.published_by.id;
		}
		if (ensure.object(this.origPost.category).id !== ensure.object(this.post.category).id) {
			toPatch.category = this.post.category.id;
		}
		if (ensure.object(this.origPost.classification).id !== ensure.object(this.classification).id) {
			toPatch.classification_id = ensure.object(this.classification).id || 0;
		}
		if (this.origImageUrl != ensure.object(this.image).url) {
			toPatch.image = this.image ? this.image.url : false;
		}
		if (!moment(this.origPost.published_at).isSame(this.post.published_at)) {
			toPatch.published_at = this.post.published_at;
		}
		if (!moment(this.origPost.source_date).isSame(this.post.source_date)) {
			toPatch.source_date = this.post.source_date;
		}
		if (this.origPost.archive_selection !== this.post.archive_selection) {
			const archiveData = this.AutoArchive.getDateAndActionFromArchiveSelection(this.post.archive_selection);
			toPatch.archive_at = archiveData.archive_at;
			toPatch.archive_action = archiveData.archive_action;
		}
		const tagsDiff = this._getTagsDiff();
		if (tagsDiff) {
			toPatch.tags = tagsDiff;
		}
		if (this.origPost.budget.amount !== this.post.budget.amount) {
			toPatch.budget = this.post.budget.amount;
		}
		return toPatch;
	}

	_getTagsDiff() {
		const origTags = _.map(this.origPost.tags, 'name').filter(Boolean).sort();
		const chosenTags = _.map(this.post.tags, 'name').filter(Boolean).sort();
		return JSON.stringify(origTags) === JSON.stringify(chosenTags) ? undefined : chosenTags;
	}

	_getAttachmentsDiff() {
		const toAdd = [];
		const toRemove = [];
		const origUrls = _.map(this.origAttachments, 'url');
		const urls = _.map(this.attachments, 'url');
		this.origAttachments.forEach(att => {
			if (urls.indexOf(att.url) == -1) {
				toRemove.push(att.id);
			}
		});
		this.attachments.forEach(att => {
			if (origUrls.indexOf(att.url) == -1) {
				toAdd.push(att);
			}
		});
		const shouldUpdateSort = origUrls.join(',') !== urls.join(',');
		return { toAdd, toRemove, shouldUpdateSort };
	}

	_getAiKeywordsDiff() {
		const toRemove = [];
		const keywords = _.map(this.keywords, 'keyword');
		ensure.array(this.origKeywords).forEach(kw => {
			if (keywords.indexOf(kw.keyword) == -1) {
				toRemove.push(kw);
			}
		});
		return { toRemove };
	}

	/**
	 * Get the currently-selected taxonomies
	 * @returns Array  the `taxonomy_option_id`s of the selected criteria. The array is empty if no changes have been made.
	 */
	_getTaxonomyOptionsDiff() {
		if (Object.keys(this.updatedTaxonomies).length === 0) {
			return [];
		}
		// `this.originalTaxonomies` and `this.originalTaxonomies` are objects. Their keys are the taxonomy names and
		/// the values are arrays of taxonomy_options.id values.
		const diff = { ...this.originalTaxonomies, ...this.updatedTaxonomies };
		// turn all the arrays into a single array
		let selectedTaxonomies = [].concat.apply([], Object.values(diff));
		return selectedTaxonomies.filter(id => id !== null);
	}
}
